import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { format, addDays, startOfDay, addMinutes, isBefore } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Loader2, ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { siteImages } from "@/lib/siteData";
import type { Database } from "@/integrations/supabase/types";
import { PromoInput } from "@/components/PromoInput";
import { getAttribution } from "@/lib/attribution";

type Studio = Database["public"]["Tables"]["studios"]["Row"];
type Addon = Database["public"]["Tables"]["addons"]["Row"];
type AddonOption = Database["public"]["Tables"]["addon_options"]["Row"];

type ProductCtx = {
  id: string;
  name: string;
  price: number;
  variantId: string | null;
  variantLabel: string | null;
  variantPrice: number | null;
};

const HOURS = Array.from({ length: 13 }, (_, i) => 9 + i); // 9am – 9pm
const DURATIONS = [1, 2, 3, 4, 6, 8];

const fmtMoney = (n: number) => `$${n.toFixed(2)}`;

const BookingPage = () => {
  const [params] = useSearchParams();
  const studioParam = params.get("studio");
  const productParam = params.get("product");
  const variantParam = params.get("variant");
  const durationParam = params.get("duration");

  const [step, setStep] = useState(1);
  const [studios, setStudios] = useState<Studio[]>([]);
  const [addons, setAddons] = useState<Addon[]>([]);
  const [addonOptions, setAddonOptions] = useState<Record<string, AddonOption[]>>({});
  const [pickedOptions, setPickedOptions] = useState<Record<string, Set<string>>>({});
  const [studioId, setStudioId] = useState<string>("");
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [hour, setHour] = useState<number | null>(null);
  const [duration, setDuration] = useState<number>(2);
  const [pickedAddons, setPickedAddons] = useState<Record<string, number>>({});
  const [bookedSlots, setBookedSlots] = useState<{ start_at: string; end_at: string }[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [customer, setCustomer] = useState({ full_name: "", email: "", phone: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [productCtx, setProductCtx] = useState<ProductCtx | null>(null);
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState<{ code: string; amount_off: number; new_total: number } | null>(null);

  // Surface a friendly notice if the user came back from Stripe via cancel.
  useEffect(() => {
    if (params.get("cancelled") === "1") {
      toast.error("Checkout cancelled. Your booking is still pending — you can pay again from step 4.");
    }
  }, [params]);

  // Load studios + addons
  useEffect(() => {
    (async () => {
      const [{ data: s }, { data: a }] = await Promise.all([
        supabase.from("studios").select("*").eq("active", true).order("display_order"),
        supabase.from("addons").select("*").eq("active", true).is("product_id", null).is("studio_id", null).order("display_order"),
      ]);
      setStudios(s ?? []);
      // Only show truly unassigned globals here (no link rows); the rest load when studio/product is known.
      const ids = (a ?? []).map(x => x.id);
      if (ids.length) {
        const [{ data: ls }, { data: lp }] = await Promise.all([
          supabase.from("addon_studios").select("addon_id").in("addon_id", ids),
          supabase.from("addon_products").select("addon_id").in("addon_id", ids),
        ]);
        const assigned = new Set<string>([
          ...((ls ?? []).map((x: any) => x.addon_id)),
          ...((lp ?? []).map((x: any) => x.addon_id)),
        ]);
        setAddons((a ?? []).filter(x => !assigned.has(x.id)));
      } else {
        setAddons([]);
      }
      if (studioParam && s) {
        const match = s.find(x => x.slug === studioParam);
        if (match) setStudioId(match.id);
      }
    })();
  }, [studioParam]);

  // Load studio-specific add-ons (legacy + linked) when a studio is chosen.
  useEffect(() => {
    if (!studioId) return;
    (async () => {
      const { data: legacy } = await supabase
        .from("addons").select("*").eq("active", true).eq("studio_id", studioId).order("display_order");
      const { data: links } = await supabase
        .from("addon_studios").select("addon_id").eq("studio_id", studioId);
      const linkIds = (links ?? []).map((x: any) => x.addon_id);
      let linked: Addon[] = [];
      if (linkIds.length) {
        const { data } = await supabase.from("addons").select("*").in("id", linkIds).eq("active", true).order("display_order");
        linked = data ?? [];
      }
      const merged = [...(legacy ?? []), ...linked];
      if (merged.length) {
        setAddons((prev) => {
          const ids = new Set(prev.map((x) => x.id));
          return [...prev, ...merged.filter((x) => !ids.has(x.id))];
        });
      }
    })();
  }, [studioId]);

  // Load product context (when arriving from /shop/:slug)
  useEffect(() => {
    if (!productParam) { setProductCtx(null); return; }
    (async () => {
      const [{ data: p }, { data: v }] = await Promise.all([
        supabase.from("products")
          .select("id,name,price,bookable_studio_id,min_hours")
          .eq("id", productParam).maybeSingle(),
        variantParam
          ? supabase.from("product_variants").select("id,size,price").eq("id", variantParam).maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      if (!p) return;
      setProductCtx({
        id: p.id,
        name: p.name,
        price: Number(p.price),
        variantId: (v as { id: string } | null)?.id ?? null,
        variantLabel: (v as { size: string | null } | null)?.size ?? null,
        variantPrice: v ? Number((v as { price: number | null }).price ?? p.price) : null,
      });
      // Load product-specific add-ons (legacy + linked)
      const { data: legacy } = await supabase
        .from("addons").select("*").eq("active", true).eq("product_id", p.id).order("display_order");
      const { data: links } = await supabase
        .from("addon_products").select("addon_id").eq("product_id", p.id);
      const linkIds = (links ?? []).map((x: any) => x.addon_id);
      let linked: Addon[] = [];
      if (linkIds.length) {
        const { data } = await supabase.from("addons").select("*").in("id", linkIds).eq("active", true).order("display_order");
        linked = data ?? [];
      }
      const merged = [...(legacy ?? []), ...linked];
      if (merged.length) {
        setAddons((prev) => {
          const ids = new Set(prev.map((x) => x.id));
          return [...prev, ...merged.filter((x) => !ids.has(x.id))];
        });
      }
      // jump past studio step + apply duration
      const d = durationParam ? Math.max(1, parseInt(durationParam, 10) || 1) : (p.min_hours || 1);
      setDuration(d);
      if (p.bookable_studio_id) {
        setStudioId(p.bookable_studio_id);
        setStep(2);
      }
    })();
  }, [productParam, variantParam, durationParam]);

  const studio = useMemo(() => studios.find(s => s.id === studioId), [studios, studioId]);

  // Load options for any addons that are now visible
  useEffect(() => {
    const missing = addons.map(a => a.id).filter(id => !(id in addonOptions));
    if (!missing.length) return;
    (async () => {
      const { data } = await supabase.from("addon_options").select("*").in("addon_id", missing).eq("active", true).order("display_order");
      const next: Record<string, AddonOption[]> = {};
      missing.forEach(id => { next[id] = []; });
      (data ?? []).forEach(o => { (next[o.addon_id] ??= []).push(o); });
      setAddonOptions(prev => ({ ...prev, ...next }));
    })();
  }, [addons]);

  // Load booked slots for selected studio + date
  useEffect(() => {
    if (!studioId || !date) { setBookedSlots([]); return; }
    setLoadingSlots(true);
    const from = startOfDay(date).toISOString();
    const to = addDays(startOfDay(date), 1).toISOString();
    supabase.rpc("get_booked_slots", { _studio_id: studioId, _from: from, _to: to }).then(({ data }) => {
      setBookedSlots((data ?? []) as any);
      setLoadingSlots(false);
    });
  }, [studioId, date]);

  const isHourAvailable = (h: number) => {
    if (!date) return false;
    const slotStart = addMinutes(startOfDay(date), h * 60);
    const slotEnd = addMinutes(slotStart, duration * 60);
    if (isBefore(slotStart, new Date())) return false;
    return !bookedSlots.some(b => {
      const bs = new Date(b.start_at).getTime();
      const be = new Date(b.end_at).getTime();
      return slotStart.getTime() < be && slotEnd.getTime() > bs;
    });
  };

  const subtotal = useMemo(() => {
    const baseCost = productCtx
      ? (productCtx.variantPrice ?? productCtx.price)
      : (studio?.hourly_rate ? Number(studio.hourly_rate) : 0) * duration;
    const addonCost = addons.reduce((sum, a) => {
      const opts = addonOptions[a.id] ?? [];
      const picked = pickedOptions[a.id];
      if (opts.length && picked && picked.size) {
        let s = 0;
        picked.forEach(oid => {
          const o = opts.find(x => x.id === oid);
          if (o) s += Number(a.price) + Number(o.price_delta);
        });
        return sum + s;
      }
      return sum + (pickedAddons[a.id] ?? 0) * Number(a.price);
    }, 0);
    return baseCost + addonCost;
  }, [studio, duration, addons, pickedAddons, pickedOptions, addonOptions, productCtx]);

  const canNext1 = !!studioId;
  const canNext2 = !!date && hour !== null;
  const canNext3 = customer.full_name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(customer.email);

  const submit = async () => {
    if (!studio || !date || hour === null) return;
    if (submittingRef.current) return; // hard double-submit guard
    submittingRef.current = true;
    setSubmitting(true);
    setCheckoutUrl(null);
    try {
      const start = addMinutes(startOfDay(date), hour * 60);
      const end = addMinutes(start, duration * 60);

      // Acquire a short-lived slot lock first so two concurrent customers
      // can't race the booking insert. Lock auto-expires; release after Stripe URL.
      const sessionToken = `bk_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      const { error: lockErr } = await supabase.rpc("acquire_slot_lock" as any, {
        _studio_id: studio.id, _start: start.toISOString(), _end: end.toISOString(),
        _session_token: sessionToken, _ttl_seconds: 900,
      });
      if (lockErr) {
        const msg = /slot_taken|slot_locked|23P01/i.test(lockErr.message)
          ? "That time slot was just taken. Please pick another time."
          : lockErr.message;
        throw new Error(msg);
      }

      const { data: booking, error } = await supabase.from("bookings").insert({
        studio_id: studio.id,
        start_at: start.toISOString(),
        end_at: end.toISOString(),
        subtotal,
        total: subtotal,
        status: "pending",
        payment_status: "unpaid",
        amount_paid: 0,
        notes: [
          `${customer.full_name} <${customer.email}>${customer.phone ? ` · ${customer.phone}` : ""}`,
          productCtx ? `Product: ${productCtx.name}${productCtx.variantLabel ? ` — ${productCtx.variantLabel}` : ""} [product:${productCtx.id}${productCtx.variantId ? ` variant:${productCtx.variantId}` : ""}]` : "",
          customer.notes,
        ].filter(Boolean).join("\n"),
      }).select("id").single();

      if (error || !booking) {
        const msg = (error as any)?.code === "23P01" || /exclude|overlap|conflicts/i.test(error?.message ?? "")
          ? "That time slot was just taken. Please pick another time."
          : (error?.message ?? "Booking failed");
        throw new Error(msg);
      }

      // Insert add-ons (quantity rows + per-option rows)
      const addonRows: any[] = [];
      addons.forEach(a => {
        const opts = addonOptions[a.id] ?? [];
        const picked = pickedOptions[a.id];
        if (opts.length && picked && picked.size) {
          picked.forEach(oid => {
            const o = opts.find(x => x.id === oid);
            if (!o) return;
            addonRows.push({
              booking_id: booking.id, addon_id: a.id, quantity: 1,
              unit_price: Number(a.price) + Number(o.price_delta),
              option_id: o.id, option_label: o.label,
            });
          });
        } else {
          const q = pickedAddons[a.id] ?? 0;
          if (q > 0) {
            addonRows.push({ booking_id: booking.id, addon_id: a.id, quantity: q, unit_price: Number(a.price) });
          }
        }
      });
      if (addonRows.length) await supabase.from("booking_addons").insert(addonRows);

      // Fire-and-forget admin notification
      supabase.functions.invoke("notify-event", {
        body: { event: "booking_created", booking_id: booking.id },
      }).catch(() => {});

      // Fire-and-forget shared calendar sync (creates a pending event)
      supabase.functions.invoke("gcal-shared-sync", {
        body: { booking_id: booking.id, action: "upsert" },
      }).catch(() => {});

      // Create checkout session
      const successUrl = `${window.location.origin}/book/success`;
      const cancelUrl = `${window.location.origin}/book?cancelled=1`;
      const { data, error: fnError } = await supabase.functions.invoke("stripe-checkout-booking", {
        body: {
          booking_id: booking.id, success_url: successUrl, cancel_url: cancelUrl,
          promo_code: promoApplied?.code,
          attribution: getAttribution(),
          customer_email: customer.email,
          customer_name: customer.full_name,
        },
      });
      if (fnError) {
        const detail = (fnError as any)?.message || "Unknown error";
        throw new Error(`Couldn't start Stripe checkout: ${detail}`);
      }
      if (data?.error) throw new Error(`Stripe checkout error: ${data.error}`);
      if (!data?.url) throw new Error("Stripe didn't return a checkout URL. Please try again.");
      setCheckoutUrl(data.url);
      // Stripe Checkout blocks iframe embedding (X-Frame-Options: DENY).
      // In production the app is top-level, so same-tab redirect works and
      // keeps the customer on-site. Only open a new tab when we're embedded
      // in an iframe (e.g. Lovable preview), otherwise the page would freeze.
      const isFramed = window.self !== window.top;
      if (isFramed) {
        const win = window.open(data.url, "_blank");
        if (!win) {
          // Popup blocked — surface a recoverable CTA instead of stalling.
          try {
            window.top!.location.href = data.url;
          } catch {
            toast.error("Popup blocked. Click 'Continue to Stripe' below to finish payment.");
            setSubmitting(false);
            submittingRef.current = false;
            return;
          }
        }
        setSubmitting(false);
        submittingRef.current = false;
      } else {
        window.location.href = data.url;
        // Leave submitting=true; we're navigating away.
      }
    } catch (e) {
      const msg = (e as Error).message || "Something went wrong. Please try again.";
      toast.error(msg);
      console.error("[booking submit]", e);
      setSubmitting(false);
      submittingRef.current = false;
    }
  };

  return (
    <div className="pt-20">
      <Helmet>
        <title>Book a Studio | MyStudio Columbus, GA</title>
        <meta name="description" content="Reserve Studio A or Studio B online in Columbus, GA. Pick your date, time, and add-ons — pay securely and get instant confirmation." />
        <link rel="canonical" href="https://bookmystudiospace.com/book" />
        <meta property="og:title" content="Book a Studio | MyStudio Columbus, GA" />
        <meta property="og:description" content="Reserve Studio A or Studio B online — pick your date, time, and add-ons." />
        <meta property="og:url" content="https://bookmystudiospace.com/book" />
        <meta property="og:type" content="website" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ReservationPackage",
          name: "Studio Rental",
          provider: { "@type": "LocalBusiness", name: "MyStudio", url: "https://bookmystudiospace.com" },
          url: "https://bookmystudiospace.com/book",
        })}</script>
      </Helmet>
      {/* Hero */}
      <section className="relative h-[30vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${siteImages.heroSlider1})` }} />
        <div className="absolute inset-0 bg-background/75" />
        <div className="relative z-10 text-center">
          <h1 className="text-3xl md:text-5xl font-display font-extrabold uppercase tracking-widest">
            {productCtx ? `BOOK ${productCtx.name.toUpperCase()}` : "BOOK THE STUDIO"}
          </h1>
          <p className="text-primary font-mono text-xs tracking-[0.3em] uppercase mt-3">STEP {step} OF 4</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Stepper bar */}
          <div className="flex items-center justify-between mb-10">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="flex-1 flex items-center">
                <div className={`w-8 h-8 flex items-center justify-center font-mono text-xs ${step >= n ? "bg-primary text-primary-foreground" : "bg-panel text-muted-foreground border border-border"}`}>
                  {step > n ? <Check className="w-4 h-4" /> : n}
                </div>
                {n < 4 && <div className={`flex-1 h-px mx-2 ${step > n ? "bg-primary" : "bg-border"}`} />}
              </div>
            ))}
          </div>

          {/* Step 1: Studio */}
          {step === 1 && (
            <div>
              <h2 className="font-display tracking-widest text-xl mb-6">CHOOSE A STUDIO</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {studios.map((s) => (
                  <button key={s.id} onClick={() => setStudioId(s.id)}
                    className={`text-left bg-panel border p-5 transition-colors ${studioId === s.id ? "border-primary" : "border-border hover:border-primary/50"}`}>
                    <h3 className="font-display tracking-wider text-lg">{s.name}</h3>
                    <p className="text-xs text-muted-foreground font-mono mt-1">{s.tagline}</p>
                    <p className="text-primary font-display mt-3">{fmtMoney(Number(s.hourly_rate))}/hr</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Date + Time + Duration */}
          {step === 2 && studio && (
            <div className="space-y-6">
              <h2 className="font-display tracking-widest text-xl">PICK A DATE & TIME</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-panel border border-border p-3">
                  <Calendar
                    mode="single"
                    selected={date}
                    onSelect={(d) => { setDate(d); setHour(null); }}
                    disabled={(d) => isBefore(d, startOfDay(new Date()))}
                    className="pointer-events-auto"
                  />
                </div>
                <div>
                  <Label className="font-mono text-xs uppercase tracking-widest mb-2 block">Duration</Label>
                  {productCtx ? (
                    <div className="mb-6 px-3 py-2 border border-border bg-panel font-mono text-xs uppercase tracking-widest">
                      {duration}h · locked by {productCtx.variantLabel ?? productCtx.name}
                    </div>
                  ) : (
                  <div className="grid grid-cols-3 gap-2 mb-6">
                    {DURATIONS.map(d => (
                      <button key={d} onClick={() => { setDuration(d); setHour(null); }}
                        className={`py-2 text-xs font-mono uppercase border transition-colors ${duration === d ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`}>
                        {d}h
                      </button>
                    ))}
                  </div>
                  )}
                  <Label className="font-mono text-xs uppercase tracking-widest mb-2 block">Start time</Label>
                  {!date ? <p className="text-xs text-muted-foreground font-body">Pick a date first.</p> : loadingSlots ? <Loader2 className="w-5 h-5 animate-spin text-primary" /> : (
                    <div className="grid grid-cols-3 gap-2">
                      {HOURS.map(h => {
                        const avail = isHourAvailable(h);
                        const sel = hour === h;
                        return (
                          <button key={h} disabled={!avail} onClick={() => setHour(h)}
                            className={`py-2 text-xs font-mono border transition-colors ${sel ? "bg-primary text-primary-foreground border-primary" : avail ? "border-border hover:border-primary/50" : "border-border/50 text-muted-foreground/40 cursor-not-allowed line-through"}`}>
                            {h % 12 === 0 ? 12 : h % 12}{h < 12 ? "AM" : "PM"}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Add-ons + Customer */}
          {step === 3 && (
            <div className="space-y-8">
              <div>
                <h2 className="font-display tracking-widest text-xl mb-4">ADD-ONS (OPTIONAL)</h2>
                {addons.length === 0 ? <p className="text-xs text-muted-foreground font-body">No add-ons available.</p> : (
                  <div className="space-y-2">
                    {addons.map(a => {
                      const opts = addonOptions[a.id] ?? [];
                      const q = pickedAddons[a.id] ?? 0;
                      if (opts.length) {
                        const picked = pickedOptions[a.id] ?? new Set<string>();
                        return (
                          <div key={a.id} className="bg-panel border border-border p-4">
                            <div className="flex items-center justify-between mb-2">
                              <p className="font-body text-sm">{a.name}</p>
                              <p className="text-xs text-muted-foreground font-mono">from {fmtMoney(Number(a.price))}</p>
                            </div>
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                              {opts.map(o => {
                                const on = picked.has(o.id);
                                const total = Number(a.price) + Number(o.price_delta);
                                const atCap = !on && a.max_quantity != null && picked.size >= a.max_quantity;
                                return (
                                  <button key={o.id} disabled={atCap} onClick={() => {
                                    const n = new Set(picked);
                                    if (n.has(o.id)) n.delete(o.id); else n.add(o.id);
                                    setPickedOptions({ ...pickedOptions, [a.id]: n });
                                  }}
                                    className={`text-left p-2 border text-xs font-mono transition-colors ${on ? "border-primary bg-primary/10 text-primary" : atCap ? "border-border/50 text-muted-foreground/40 cursor-not-allowed" : "border-border hover:border-primary/50"}`}>
                                    <div>{o.label}</div>
                                    <div className="text-[10px] opacity-70">{fmtMoney(total)}</div>
                                  </button>
                                );
                              })}
                            </div>
                            {a.max_quantity != null && (
                              <p className="text-[10px] text-muted-foreground font-mono mt-2 uppercase tracking-widest">
                                Pick up to {a.max_quantity} · {picked.size}/{a.max_quantity} selected
                              </p>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div key={a.id} className="bg-panel border border-border p-4 flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="font-body text-sm">{a.name}</p>
                            <p className="text-xs text-muted-foreground font-mono">{fmtMoney(Number(a.price))} {a.pricing_unit !== "flat" ? `/ ${a.pricing_unit}` : ""}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setPickedAddons({ ...pickedAddons, [a.id]: Math.max(0, q - 1) })} className="w-7 h-7 border border-border font-mono">−</button>
                            <span className="w-6 text-center font-mono text-sm">{q}</span>
                            <button
                              onClick={() => {
                                const cap = a.max_quantity ?? Infinity;
                                setPickedAddons({ ...pickedAddons, [a.id]: Math.min(cap, q + 1) });
                              }}
                              disabled={a.max_quantity != null && q >= a.max_quantity}
                              className="w-7 h-7 border border-border font-mono disabled:opacity-30"
                            >+</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <h2 className="font-display tracking-widest text-xl mb-2">YOUR INFO</h2>
                <div><Label className="font-mono text-xs uppercase tracking-widest">Full name *</Label><Input value={customer.full_name} onChange={e => setCustomer({ ...customer, full_name: e.target.value })} /></div>
                <div><Label className="font-mono text-xs uppercase tracking-widest">Email *</Label><Input type="email" value={customer.email} onChange={e => setCustomer({ ...customer, email: e.target.value })} /></div>
                <div><Label className="font-mono text-xs uppercase tracking-widest">Phone</Label><Input value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })} /></div>
                <div><Label className="font-mono text-xs uppercase tracking-widest">Notes</Label><Textarea rows={3} value={customer.notes} onChange={e => setCustomer({ ...customer, notes: e.target.value })} placeholder="Anything we should know about your shoot…" /></div>
              </div>
            </div>
          )}

          {/* Step 4: Review + pay */}
          {step === 4 && studio && date && hour !== null && (
            <div>
              <h2 className="font-display tracking-widest text-xl mb-6">REVIEW & PAY</h2>
              <div className="bg-panel border border-border p-6 space-y-3 mb-6">
                {productCtx && (
                  <Row label="Product" value={`${productCtx.name}${productCtx.variantLabel ? ` — ${productCtx.variantLabel}` : ""}`} />
                )}
                <Row label="Studio" value={studio.name} />
                <Row label="Date" value={format(date, "EEE, MMM d yyyy")} />
                <Row label="Time" value={`${hour % 12 === 0 ? 12 : hour % 12}${hour < 12 ? "AM" : "PM"} · ${duration}h`} />
                {productCtx
                  ? <Row label="Package price" value={fmtMoney(productCtx.variantPrice ?? productCtx.price)} />
                  : <Row label="Studio rate" value={`${fmtMoney(Number(studio.hourly_rate))}/hr × ${duration}h`} />
                }
                {addons.flatMap(a => {
                  const opts = addonOptions[a.id] ?? [];
                  const picked = pickedOptions[a.id];
                  if (opts.length && picked && picked.size) {
                    return [...picked].map(oid => {
                      const o = opts.find(x => x.id === oid);
                      if (!o) return null;
                      const total = Number(a.price) + Number(o.price_delta);
                      return <Row key={a.id + oid} label={`${a.name}: ${o.label}`} value={fmtMoney(total)} />;
                    }).filter(Boolean) as any;
                  }
                  const q = pickedAddons[a.id] ?? 0;
                  return q > 0 ? [<Row key={a.id} label={`${a.name} × ${q}`} value={fmtMoney(Number(a.price) * q)} />] : [];
                })}
                <div className="border-t border-border pt-3 flex items-center justify-between">
                  <span className="font-display tracking-widest">TOTAL</span>
                  <span className="font-display text-primary text-xl">{fmtMoney(subtotal)}</span>
                </div>
                <PromoInput context="booking" subtotal={subtotal} value={promoCode} onApplied={setPromoApplied} onChange={setPromoCode} applied={promoApplied} />
                {promoApplied && (
                  <div className="flex items-center justify-between text-xs text-primary mt-1">
                    <span>Discount ({promoApplied.code})</span>
                    <span>−{fmtMoney(promoApplied.amount_off)}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground font-mono uppercase tracking-widest mb-6">100% PAYMENT UPFRONT VIA STRIPE. NO HOLDS.</p>
            </div>
          )}

          {/* Footer nav */}
          <div className="flex items-center justify-between mt-10">
            <Button variant="outline" onClick={() => setStep(s => Math.max(1, s - 1))} disabled={step === 1 || submitting}>
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            {step < 4 ? (
              <Button onClick={() => setStep(s => s + 1)} disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2) || (step === 3 && !canNext3)}
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-widest">
                Next <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <div className="flex flex-col items-end gap-2">
                <Button onClick={submit} disabled={submitting} aria-busy={submitting} className="bg-primary text-primary-foreground hover:bg-primary/90 font-display tracking-widest px-8">
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {submitting ? "REDIRECTING…" : `PAY ${fmtMoney(subtotal)}`}
                </Button>
                {checkoutUrl && (
                  <a
                    href={checkoutUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-mono uppercase tracking-widest text-primary underline"
                  >
                    Continue to Stripe →
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

const Row = ({ label, value }: { label: string; value: string }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-muted-foreground font-body">{label}</span>
    <span className="font-body">{value}</span>
  </div>
);

export default BookingPage;
